/**
 * @author jakubbujakowski
 * @since 2021-10-28
 */

import * as React from "react";
import styled from "styled-components";

import { Input } from "@ui/Atoms";
import { rem, rgba, transition } from "@ui/helpers";
import { scaleIcon } from "@ui/helpers/icon";
import { fadeIn } from "@ui/animations";
import { Clock } from "@ui/Assets/Symbolicons";

import { useOnClickOutside } from "@hooks/useOnClickOutside";

type Timeslot = { value: string; label: string };

interface Props {
  timeslots: Timeslot[];
  activeSlot: string;
  inputLabelText: string;
  disableBefore?: string;
  disableAfter?: string;
  name?: string;
  calendarAlignment?: "left" | "right";
  onTimeslotClick: ({ value, label }: Timeslot) => void;
}

const ScaledClock = scaleIcon(Clock, 1.5);

const hourToNum = (hour: string) => Number(hour.split(":").join(""));

const HourPicker = (props: Props): React.ReactElement => {
  const inputRef = React.useRef<HTMLInputElement>();
  const [isCalendarVisible, setIsCalendarVisible] =
    React.useState<boolean>(false);

  const isDisabledInPast = (value: string) => {
    if (props.disableBefore) {
      return hourToNum(value) < hourToNum(props.disableBefore);
    }
    return false;
  };

  const isDisabledInFuture = (value: string) => {
    if (props.disableAfter) {
      return hourToNum(value) > hourToNum(props.disableAfter);
    }
    return false;
  };

  const handleTimeslotClick = (timeslot: Timeslot) => {
    setIsCalendarVisible(false);
    props.onTimeslotClick(timeslot);
  };

  const ref = useOnClickOutside<HTMLDivElement>(() => {
    setIsCalendarVisible(false);
  });

  React.useEffect(() => {
    if (!isCalendarVisible) {
      inputRef.current?.blur();
    }
  }, [isCalendarVisible]);

  return (
    <div ref={ref}>
      <InputWrapper>
        <StyledInput
          refElement={inputRef}
          name={props.name || "hour-picker"}
          icon={<ScaledClock variant="line" />}
          readOnly
          value={props.activeSlot}
          onClick={() => setIsCalendarVisible(!isCalendarVisible)}
          label={props.inputLabelText}
          onBlur={() => {
            // prevent input from losing focus when calendar is visible
            if (isCalendarVisible) {
              inputRef.current?.focus();
            }
          }}
        />
        {isCalendarVisible && (
          <Calendar alignment={props.calendarAlignment}>
            <TilesWrapper>
              {props.timeslots.map((slot, idx) => {
                const isDisabled =
                  isDisabledInPast(slot.value) ||
                  isDisabledInFuture(slot.value);

                return (
                  <Tile
                    key={idx}
                    isActive={slot.value === props.activeSlot}
                    isDisabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) {
                        handleTimeslotClick(slot);
                      }
                    }}
                  >
                    {slot.label}
                  </Tile>
                );
              })}
            </TilesWrapper>
          </Calendar>
        )}
      </InputWrapper>
    </div>
  );
};

HourPicker.displayName = "HourPicker";

export { HourPicker };

const StyledInput = styled(Input)`
  cursor: pointer;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const TilesWrapper = styled.ul`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  grid-gap: ${props => props.theme.margins.base};
  list-style-type: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 0;
  }
`;

const Tile = styled.li<{ isActive: boolean; isDisabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(40)};
  width: ${rem(52)};
  border: 1px solid ${props => props.theme.palette.blue100};
  border-radius: ${props => props.theme.buttons.borderRadius};
  cursor: pointer;
  transition: ${transition(["background", "color", "border"])};

  &:hover {
    background: ${props => props.theme.palette.blue500};
    border-color: ${props => props.theme.palette.blue500};
    color: ${props => props.theme.palette.white};
    transition: ${transition(["background", "color", "border"])};
  }

  ${props =>
    props.isActive &&
    `
    background: ${props.theme.palette.blue500};
    border-color: ${props.theme.palette.blue500};
    color: ${props.theme.palette.white};
  `}

  ${props =>
    props.isDisabled &&
    `
    border-color: ${props.theme.palette.grey100};
    color: ${props.theme.palette.grey100};
    pointer-events: none;
  `}
`;

const Calendar = styled.div<{ alignment?: "left" | "right" }>`
  position: absolute;
  top: ${rem(64)};
  width: ${rem(328)};
  height: ${rem(312)};
  padding: ${props => props.theme.margins.base_x2};
  background: ${props => props.theme.palette.white};
  border-radius: ${props => props.theme.buttons.borderRadius};
  box-shadow: ${props =>
    `0 ${rem(2)} ${rem(3)} ${rem(2)} ${rgba(
      props.theme.palette.grey700,
      0.2,
    )}`};
  animation: ${fadeIn} ${props => props.theme.animations.duration} forwards;
  z-index: ${props => props.theme.zIndex.modal};

  ${props =>
    props.alignment === "right"
      ? `
    right: 0;
  `
      : `left: 0`}
`;
